import { useGame } from "../em2c-store";
import { EM2C_BUILDINGS } from "../data/em2c-buildings";
import { getNeighbors } from "../hexgrid";
import { HYDRATED_MINERAL_KIND } from "../data/resourceSystemRules";

export const HYDRATED_ZONE_TARGET_HEXES = 20;
export const HYDRATED_WATER_CAPACITY_PER_HEX = 1200;

let installed = false;
let unsubscribe = null;
let migrationInProgress = false;
let depletionUpdateInProgress = false;

const keyOf = (q, r) => `${q},${r}`;
const clamp01 = (v) => Math.max(0, Math.min(1, Number(v) || 0));

function zoneCentroid(hexes) {
  if (!hexes.length) return { x: 0, z: 0 };
  const total = hexes.reduce((acc, h) => ({ x: acc.x + (h.x || 0), z: acc.z + (h.z || 0) }), { x: 0, z: 0 });
  return { x: total.x / hexes.length, z: total.z / hexes.length };
}

function getGridHex(grid, q, r) {
  return grid?.get?.(keyOf(q, r)) || null;
}

function sourceKeysForDeposit(deposit) {
  const sourceHexes = deposit.hexes?.length ? deposit.hexes : [{ q: deposit.q, r: deposit.r }];
  return new Set(sourceHexes.map((h) => keyOf(h.q, h.r)));
}

function growConnectedZone(grid, deposit, reservedKeys, blockedSeedKeys) {
  const selected = [];
  const selectedKeys = new Set();
  const sourceHexes = deposit.hexes?.length
    ? deposit.hexes
    : [{ q: deposit.q, r: deposit.r }];

  for (const source of sourceHexes) {
    const hex = getGridHex(grid, source.q, source.r);
    if (!hex) continue;
    const key = keyOf(hex.q, hex.r);
    if (selectedKeys.has(key) || reservedKeys.has(key) || blockedSeedKeys.has(key)) continue;
    if (hex.hasDeposit && !["water", HYDRATED_MINERAL_KIND].includes(hex.hasDeposit)) continue;
    selected.push(hex);
    selectedKeys.add(key);
  }

  while (selected.length < HYDRATED_ZONE_TARGET_HEXES) {
    const candidates = new Map();
    for (const base of selected) {
      for (const n of getNeighbors(base.q, base.r)) {
        const key = keyOf(n.q, n.r);
        if (selectedKeys.has(key) || reservedKeys.has(key) || blockedSeedKeys.has(key) || candidates.has(key)) continue;
        const hex = getGridHex(grid, n.q, n.r);
        if (!hex || !hex.buildable || hex.hazard) continue;
        if (hex.hasDeposit && !["water", HYDRATED_MINERAL_KIND].includes(hex.hasDeposit)) continue;
        candidates.set(key, hex);
      }
    }

    if (!candidates.size) break;

    // Stable ordering makes the geological field deterministic across reloads.
    const next = [...candidates.values()].sort((a, b) => {
      const da = Math.abs(a.q - deposit.q) + Math.abs(a.r - deposit.r);
      const db = Math.abs(b.q - deposit.q) + Math.abs(b.r - deposit.r);
      return da - db || a.q - b.q || a.r - b.r;
    })[0];

    selected.push(next);
    selectedKeys.add(keyOf(next.q, next.r));
  }

  for (const key of selectedKeys) reservedKeys.add(key);
  return selected;
}

export function migrateWaterDepositsToHydratedZones(state) {
  const grid = state.grid;
  if (!grid?.values) return { deposits: state.deposits || [], objectives: state.objectives || [] };

  const sourceDeposits = state.deposits || [];
  const waterLikeDeposits = sourceDeposits.filter((deposit) => ["water", HYDRATED_MINERAL_KIND].includes(deposit.kind));
  const originalZoneSeeds = new Map(waterLikeDeposits.map((deposit) => [deposit.id, sourceKeysForDeposit(deposit)]));

  // Remove the retired water-deposit identity before rebuilding the connected
  // Hydrated Mineral Zones. Metal and other geology is left untouched.
  for (const hex of grid.values()) {
    if (["water", HYDRATED_MINERAL_KIND].includes(hex.hasDeposit)) {
      hex.hasDeposit = null;
      hex.depositId = null;
    }
  }

  const reservedKeys = new Set();
  const migratedDeposits = [];

  for (const deposit of sourceDeposits) {
    if (!["water", HYDRATED_MINERAL_KIND].includes(deposit.kind)) {
      migratedDeposits.push(deposit);
      for (const h of deposit.hexes || []) reservedKeys.add(keyOf(h.q, h.r));
      continue;
    }

    // Protect the seed/core cells of every other hydrated field so one 20-hex
    // field can never swallow the starting point of another field.
    const blockedSeedKeys = new Set();
    for (const [otherId, keys] of originalZoneSeeds.entries()) {
      if (otherId === deposit.id) continue;
      for (const key of keys) blockedSeedKeys.add(key);
    }

    const connected = growConnectedZone(grid, deposit, reservedKeys, blockedSeedKeys);
    const hexes = connected.map((hex) => ({ q: hex.q, r: hex.r, x: hex.x, z: hex.z }));
    const center = zoneCentroid(hexes);
    const capacity = hexes.length * HYDRATED_WATER_CAPACITY_PER_HEX;
    const oldCapacity = Number(deposit.capacity || 0);
    const oldRatio = oldCapacity > 0 ? clamp01(Number(deposit.remaining ?? oldCapacity) / oldCapacity) : 1;
    const remaining = capacity * oldRatio;

    const migrated = {
      ...deposit,
      kind: HYDRATED_MINERAL_KIND,
      label: "Hydrated Mineral Zone",
      q: hexes[0]?.q ?? deposit.q,
      r: hexes[0]?.r ?? deposit.r,
      x: center.x,
      z: center.z,
      hexes,
      capacity,
      remaining,
    };

    for (const hex of connected) {
      hex.hasDeposit = HYDRATED_MINERAL_KIND;
      hex.depositId = migrated.id;
    }

    migratedDeposits.push(migrated);
  }

  const objectives = (state.objectives || []).map((objective) => {
    if (objective.type !== "water_extractor" && objective.id !== "water_extractor") return objective;
    return {
      ...objective,
      label: "Build a Regolith Water Extractor on a Hydrated Mineral Zone",
    };
  });

  return { deposits: migratedDeposits, objectives };
}

function migrateCurrentState() {
  if (migrationInProgress) return;
  migrationInProgress = true;
  try {
    const state = useGame.getState();
    const migrated = migrateWaterDepositsToHydratedZones(state);
    useGame.setState(migrated);
  } finally {
    migrationInProgress = false;
  }
}

function findZoneForExtractor(deposits, building) {
  return (deposits || []).find((deposit) =>
    deposit.kind === HYDRATED_MINERAL_KIND &&
    (deposit.hexes || []).some((hex) => hex.q === building.q && hex.r === building.r)
  );
}

function depleteHydratedZones(state, previous) {
  if (depletionUpdateInProgress) return;
  const now = Number(state._totalTime || 0);
  const before = Number(previous?._totalTime || 0);
  const dt = now - before;
  if (!(dt > 0)) return;

  const extractors = (state.buildings || []).filter((building) =>
    building.type === "water_extractor" &&
    building.progress >= 1 &&
    building.active !== false &&
    building.state === "operational"
  );
  if (!extractors.length) return;

  const rate = Number(EM2C_BUILDINGS.water_extractor?.productionOutputs?.raw_water || 0);
  if (!(rate > 0)) return;

  const depletionByZone = new Map();
  for (const extractor of extractors) {
    const zone = findZoneForExtractor(state.deposits, extractor);
    if (!zone || zone.remaining <= 0) continue;
    depletionByZone.set(zone.id, (depletionByZone.get(zone.id) || 0) + rate * dt);
  }
  if (!depletionByZone.size) return;

  const newlyDepleted = [];
  const deposits = (state.deposits || []).map((deposit) => {
    const amount = depletionByZone.get(deposit.id);
    if (!amount) return deposit;
    const remaining = Math.max(0, Number(deposit.remaining || 0) - amount);
    if (deposit.remaining > 0 && remaining <= 0) newlyDepleted.push(deposit.id);
    return { ...deposit, remaining };
  });

  let buildings = state.buildings;
  if (newlyDepleted.length) {
    const depletedSet = new Set(newlyDepleted);
    buildings = state.buildings.map((building) => {
      if (building.type !== "water_extractor") return building;
      const zone = findZoneForExtractor(deposits, building);
      if (!zone || !depletedSet.has(zone.id)) return building;
      return { ...building, active: false, state: "deposit_depleted", controllerReason: "Hydrated Mineral Zone depleted" };
    });
  }

  depletionUpdateInProgress = true;
  try {
    useGame.setState({ deposits, buildings });
    if (newlyDepleted.length) {
      useGame.getState().pushNotification?.({
        level: "warning",
        text: "Hydrated Mineral Zone depleted — affected Regolith Water Extractors stopped.",
      });
    }
  } finally {
    depletionUpdateInProgress = false;
  }
}

export function installWaterGeologyIntegration() {
  if (installed) return;
  installed = true;

  const state = useGame.getState();
  const baseReset = state.reset;
  const baseLoad = state.load;
  const basePlacementCheck = state.isValidHexPlacement;

  useGame.setState({
    reset: (...args) => {
      baseReset?.(...args);
      migrateCurrentState();
    },
    load: (...args) => {
      baseLoad?.(...args);
      migrateCurrentState();
    },
    isValidHexPlacement: (...args) => {
      const result = basePlacementCheck?.(...args);
      if (!result?.reason) return result;
      if (result.reason.includes(HYDRATED_MINERAL_KIND)) {
        return { ...result, reason: "Requires Hydrated Mineral Zone" };
      }
      return result;
    },
  });

  migrateCurrentState();
  unsubscribe = useGame.subscribe((next, previous) => depleteHydratedZones(next, previous));
}

export function uninstallWaterGeologyIntegration() {
  unsubscribe?.();
  unsubscribe = null;
  installed = false;
}
